/* eslint-disable max-lines -- keep in one place */
import { EMPLOYEE_SAVINGS_MAX_COMPANY_VP_WARNING } from '@Common/constants/employee_savings';
import type { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import { EmployeeSavingsRiskProfile } from '@Common/types/profile/employee_savings';

import { formatCentsNumber } from '@sh4res/bo-common/utils';

export const b2bCampaignsTranslations = {
  dashboard: {
    answer_rate: {
      label: 'Answer rate',
      chart: {
        center_label: 'Number of employees',
      },
      legend: {
        at_least_one: 'Made at least one active choice',
        inactive: 'Inactive',
      },
    },
    total_amounts: {
      label: 'Total amounts',
      chart: {
        center_label: 'Total amounts',
      },
      legend: {
        payed_out: 'Payed out',
        invested_per_plan_category: 'Invested per plan',
        pei: 'PEI Soles / PEE',
        percoi: 'PERECO-I Soles / PERECO',
        er: 'External insurance contract',
        choice_unknown: 'Choice still unknown',
      },
      description: 'Distribution of net CSG and CRDS amounts',
    },
    payment: {
      sepa_direct_debit_date: 'SEPA Direct Debit Date',
      funds_request_date: 'Charge date',
    },
    allocations: {
      export: {
        label: 'Download report',
        detailed_label: 'Detailed export',
        file_name_draft: 'Allocations_Operation_Draft_%{date}.xlsm',
        errors: {
          report_generation_failed: 'Error generating report: %{error}',
        },
      },
    },
  },
  fields: {
    fiscal_period_end: 'Fiscal period end date',
    allocations_file: 'Allocations file',
    products: 'Product coverage',
    value_sharing_bonus_fiscal_rule: 'Value Sharing Bonus Fiscal Rule',
    value_sharing_bonus_fiscal_rule_helper:
      'Determines the tax (CSG/CRDS) calculation for this Value Sharing Bonus campaign',
    savings_funds_request_id: 'Savings funds request ID',
    total_net_amount: 'Total amount to be paid',
    bank_account: 'Bank account for direct debit',
    direct_debit_date: 'Direct debit date',
    campaign_operation_summary: 'Processing report',
    fund_request_document: 'Fund request document',
    payment_method: 'Payment method',
    group_operation_gross_amount: 'Group operation gross amount',
    reference_year: 'Reference year',
    pass_for_reference_year: 'PASS for %{year}: %{amount}',
    pass_not_found_for_year: 'No PASS index found for %{year}',
    matching_contribution_enabled: 'Matching contribution enabled',
  },
  choices: {
    type: {
      participation: 'Profit-sharing bonus',
      profit_sharing: 'Incentive bonus',
      value_sharing_bonus: 'Value-sharing bonus',
      voluntary_deposit: "Company's voluntary deposits",
      employee_voluntary_deposit: "Employee's voluntary deposits",
      unused_vacation_days: 'Time savings account bonus',
      ccb_matured_election: 'Legal deadline',
      withdrawal_at_maturity: 'Maturity payout',
      matching_contribution: 'Matching contribution',
    } satisfies Record<GetB2bCampaignOneResponse['type'] | 'employee_voluntary_deposit', string>,
    reason: {
      standard: 'Standard',
      adjustment: 'Adjustment',
      regularization: 'Regularization',
      exceptional: 'Exceptional',
      omission: 'Omission',
    } satisfies Record<NonNullable<GetB2bCampaignOneResponse['reason']>, string>,
    payment_method: {
      sepa: 'SEPA',
      transfer: 'Bank transfer',
    } satisfies Record<NonNullable<GetB2bCampaignOneResponse['payment_method']>, string>,
    status: {
      campaign_created: 'Created',
      campaign_started: 'In progress',
      campaign_ended: 'Operation ended',
      campaign_draft: 'Draft',
      campaign_draft_failed: 'Draft failed',
      campaign_published: 'Published',
      campaign_published_failed: 'Published failed',
      campaign_cancelled: 'Cancelled',
      campaign_cancelled_failed: 'Cancelled failed',
      campaign_investment_amount: 'Setting campaign investment amount',
      company_funds_await: 'Awaiting company funds',
      company_funds_received: 'Company funds received',
      finished: 'Completed',
      campaign_started_failed: 'Operation started failed',
      campaign_ended_failed: 'Operation ended failed',
      campaign_investment_amount_failed: 'Setting campaign investment amount failed',
      company_funds_await_failed: 'Awaiting company funds failed',
      company_funds_received_failed: 'Company funds received failed',
      finished_failed: 'Finished failed',
      campaign_ending_grace_period: 'Ending grace period',
      campaign_ending_grace_period_failed: 'Ending grace period failed',
      campaign_unused_vacation_days_cash_conversion: 'Allocation CET campaign cash',
      campaign_unused_vacation_days_cash_conversion_failed:
        'Failed allocation of CET campaign cash',
      campaign_pending_allocation_computation: 'Pending allocation computation',
      campaign_pending_allocation_computation_failed: 'Pending allocation computation failed',
      campaign_awaiting_customer_confirmation: 'Awaiting customer confirmation',
      campaign_awaiting_customer_confirmation_failed: 'Awaiting customer confirmation failed',
      campaign_company_allocation_choices_processed: 'Company allocation choices processed',
      campaign_company_allocation_choices_processed_failed:
        'Company allocation choices processed failed',
    } satisfies Record<
      Exclude<GetB2bCampaignOneResponse['status'], null> | 'campaign_created',
      string
    >,
    amount_type_input: {
      net: 'Total amount is net of CSG/CRDS',
      gross: 'Total amount is gross',
    },
    payment_methods: {
      sepa_linked: 'SEPA Direct Debit',
      sepa_new: 'New SEPA Direct Debit',
      transfer: 'Bank Transfer',
    },
    value_sharing_bonus_fiscal_rule: {
      with_csg_crds_deduction: 'VSB with CSG/CRDS deduction',
      exempt_from_csg_crds: 'VSB exempt from CSG/CRDS',
      without_csg_crds_deduction: 'VSB without CSG/CRDS deduction',
    },
  },
  sections: {
    affiliates: {
      title: 'Affiliates data',
      description:
        'Your contract state that the computation of the individual allocations is carried out by the account keeper following the below rule.',
      distribution_title: '%{type} distribution details',
      rule: 'Rule',
      distribution_uniformly: 'Uniformly',
      distribution_salary: 'Proportionally to salary received',
      distribution_tenure: 'Proportionally to time of presence',
      distribution_total: 'Total',
      distribution_file: 'File upload',
      distribution_file_url: 'Distribution rule file',
      distribution_file_desc:
        'In order to compute the allocations you need to upload additional affiliates data (gross salaries, actual attendance time and contractual attendance time)\n\nYour allocation setup will only be successful if your company’s affiliates list is correctly completed. If there is a discrepancy between the imported file and the current list (additional affiliates or inconsistent data), the operation will be blocked. Please check and complete the repository before performing the import.',
    },
    general: {
      title: 'General details',
      details: 'Details',
      operation_processing: 'Operation processing',
      processing_options_contract_note:
        'These values are auto-populated from the contract’s current configuration.',
      operation_amount: 'Operation amount',
      operation_amount_description:
        'This is the total envelope that you have calculated for your operation.',
      group_operation_gross_amount: 'Group operation gross amount',
      group_operation_gross_amount_description:
        'This is the total gross amount of your group operation envelope.',
      amount_type_input: 'Specify if this amount is gross or net of CSG/CRDS*: ',
      reference_fiscal_period: 'Reference fiscal period',
      fiscal_period_past: "The reference fiscal period can't be in the future",
      select_company_first: 'Please select a company first',
      no_matching_contribution_config_for_year:
        'None of the impacted plans have a matching contribution config for the selected year.',
      processing: 'Operations processing',
      errors: {
        invalid_contracts:
          'When selecting multiple contracts, all must have the same operation processing settings',
        invalid_contracts_terminated:
          'Terminated contracts cannot be used for collective operations',
        invalid_contracts_voluntary_deposit:
          "When selecting multiple contracts for voluntary deposit, they must have the same valid condition for a campaign of company's voluntary deposits",
        invalid_contracts_voluntary_deposit_removed: `Contracts that does not match a campaign of company's voluntary deposits have been removed from selected contracts`,
        invalid_contracts_cet_allocation_computed_by:
          'CET campaigns cannot have allocation computed by account keeper. Update the contract processing options.',
        no_common_cash_sources: "No common cash source on the contracts' plans selected",
        different_agreement_distributions:
          'Distribution rules are not the same on all contracts selected',
      },
    },
    review: {
      gross_amount: 'Gross Amount',
      net_amount: 'Net of CSG/CRDS amount',
      net_amount_deposit: 'Total deposit amount',
      title: 'Review',
      errors: {
        no_allocations: 'No allocations defined',
        no_contract_plans: 'There are no saving plans enabled for selected contracts',
        different_processing_options:
          'Processing options are not the same on all contracts selected',
        invalid_employee_allocations: 'Some employees allocations are not valid',
        zero_amount_allocations:
          'Some employees have an amount of €0 (total or on a day type): enter an amount for each',
        day_types_mismatch:
          "Some employees' day types in the file differ from the ones they invested in: fix the file and upload it again",
        missing_employees_link: "Verify employees's account here",
        employee_not_found: 'Employee not eligible',
        employee_not_fully_onboarded: 'Employee is not fully onboarded',
        employee_not_exists: 'Employee account does not exist with this NIR',
        total_not_match: 'Allocations do not match expected total',
        total_not_match_mc: 'Matching contribution do not match expected total',
        some_choice_at_zero:
          'Please verify the allocations choices, one or more have an amount of 0 euros.',
        some_choice_at_zero_mc:
          'Please verify the matching contribution choices, one or more have an amount of 0 euros.',
        matching_contribution_ceiling_exceeded:
          'The matching contribution exceeds the remaining ceiling for the following employees',
        matching_contribution_ceiling_exceeded_by: 'exceeds by %{amount} €',
        warning_zero_amount: 'Includes an amount of 0 euros',
        user_closed: 'User account is closed',
        duplicate_employee: 'The same affiliate appears more than once in the allocations',
      },
      warnings: {
        already_answered: 'Already made an investment choice',
        campaigns_blocked: 'Payout only (regulatory restriction)',
        campaigns_blocked_restriction: 'Payout only (campaigns blocked)',
        campaigns_blocked_employees: {
          part1:
            'Since April 12, 2022, Russian or Belarusian nationals can no longer invest in euro-denominated investment products (EU Regulation 833/2014, consolidated, concerning restrictive measures in view of Russia’s actions destabilizing the situation in Ukraine). This restrictive measure does not apply to nationals of an EU Member State or holders of a valid residence permit in an EU Member State.',
          part2:
            'The employees below were born in Russia or Belarus and are therefore presumed to hold this nationality.',
          part3: 'To allow them to make their investment choices, ',
          part3bold: 'please contact Customer Service to provide proof of exemption:',
          bullet1:
            'An identity document proving nationality (or dual nationality) of a European Union country',
          bullet2:
            'If they only hold Russian nationality, a valid residence permit in a European Union country',
          part4: 'Failing this, only immediate payout of their share may be offered.',
        },
        company_vp_too_large: `Company voluntary deposit allocation > ${formatCentsNumber(EMPLOYEE_SAVINGS_MAX_COMPANY_VP_WARNING)}`,
        title: 'Some employees allocations need extra verification',
      },
    },
    confirmation: {
      title: 'Confirmation',
      summary: 'Summary and confirmation',
      operation_type: 'Operation type',
      reference_fiscal_period: 'Reference fiscal period',
      reference_fiscal_period_value: 'From %{from} to %{to}',
      reference_year: 'Reference year',
      gross_amount: 'Total gross amount',
      net_amount: 'Total amount net of CSG/CRDS',
      net_amount_deposit: 'Total deposits amount',
      total_amount_to_be_paid: 'Total Amount to be paid',
      total_amount_to_be_value: 'will be computed at the end of consultation period',
      matching_contributions_next_fund_request:
        'This amount will be added in your next Matching Contributions Fund Request',
      matching_contributions_to_be_paid: 'Total Matching Contributions amount to be paid',
      matching_contributions_zero:
        'The total Matching Contributions amount is 0, so this campaign cannot be saved. Please check that Matching Contributions are enabled on the selected instruments.',
      matching_contributions_to_be_value: 'will be computed at the end of consultation period',
      consultation_period: 'Consultation period',
      consultation_period_value: 'From %{from} to %{to}',
      funds_request_date: 'Funds request date',
      sepa_direct_debit_date: 'Charge date',
      funds_request_date_value: '%{date} (%{hours}h after the end of the consultation period)',
      funds_request_date_value_at: 'at',
      employees_concerned: 'Number of employees concerned',
      payment_method_title: 'Payment method',
      sepa_direct_debit: 'SEPA Direct Debit',
      sepa_warning:
        'You have chosen direct debit, but no SEPA mandate is yet associated with your account. Please go to the Payment Information tab to add one and enable payment for your transaction.',
      bank_transfer: 'Bank transfer',
      late_payment_interests: 'Late payment interests amount',
      confirmations_title: 'Confirmations',
      confirm_accuracy: 'I confirm the accuracy of the data',
      confirm_sepa_debit:
        'I confirm that I agree to the direct debit of the amounts to be invested at the end of the consultation period',
      confirm_sepa_debit_company_allocator:
        'I confirm that I agree to the direct debit of %{total_amount_to_be_paid}',
      choice_collected_by: 'Choices collected by',
      payout_by: 'Payouts by',
      allocation_computed_by: 'Allocations computed by',
      matching_contribution_computed_by: 'Matching contribution computed by',
    },
    success_modal: {
      title: 'Operation created successfully',
      body: `Congratulations! Your Operation has been successfully created. You can monitor it through your Operation Dashboard.

        A summary email has also been sent to you.`,
      btn: 'Got it',
    },
    success_modal_updated: {
      title: 'Operation updated successfully',
      body: 'Your Operation has been successfully updated. You can monitor it through your Operation Dashboard.',
    },
    success_modal_unused_vacation_days: {
      body: 'For the next step, you can upload the allocations, after the consolidation period, on %{date}.',
    },
    success_modal_allocations_to_compute: {
      title: 'Operation prepared successfully',
      body: `Your Operation has been successfully prepared.
We will now compute the allocation for you. Once it is done you will be able to review it before starting it.`,
    },
    success_modal_allocations_computed: {
      title: 'Operation prepared successfully',
      body: `Operation has been successfully prepared.
It is now ready for the final review from the agent who will be able to approve and start the campaign.`,
    },
    duration_warning_modal: {
      title: 'Warning',
      body: 'You are creating an operation for which the consultation period is less than 15 days, do you want to continue anyway?',
    },
    company_collected_choices_blocking: {
      title:
        'Allocation made to different profiles within the same plans for the following affiliates',
      body: 'You cannot allocate amounts into different profiles of the same plan for the same affiliate. Please go back to the file to fix the errors then come back to finalize the operation creation.',
      affiliates: 'Affiliates impacted:',
    },
    removed_employees: {
      title: 'Affiliates will be removed from the operation',
      body: 'The affiliates below have an amount of 0 and will be removed from the operation. They will no longer be able to allocate anything, and any investment choice they have already made will be cancelled. Would you like to confirm?',
      affiliates: 'Affiliates impacted:',
    },
    company_collected_choices: {
      title: 'Allocation made to different profiles than the affiliates current ones',
      body: 'Please note that the affiliates below will receive the amounts into a different profile than their current one. Their plan will be automatically adjusted (arbitrage) to align with the latest profile. Would you like to confirm?',
      affiliates: 'Affiliates impacted:',
    },
    allocations: {
      placement_support: 'Placement support',
      matching_contribution_amount: 'Matching Contribution Amount',
      risk_profile_current: 'Current profile',
      risk_profile: {
        balanced: 'Balanced',
        careful: 'Careful',
        dynamic: 'Dynamic',
        sicav: 'SICAV retirement',
      } satisfies Record<EmployeeSavingsRiskProfile, string>,
      plan: 'Plan',
      identifier: 'Identifier',
      add_row: 'Add row',
      payouts: 'Payouts',
      ccb: 'CCB',
      title: 'Allocations',
      consultation_period: 'Consultation period',
      errors: {
        invalid_file_version: 'Invalid file version, the actual one is v%{version}.',
        invalid_plan: 'Plan %{plan} not found in chosen contracts.',
        ambiguous_plan:
          'Plan %{plan} matches several plans in chosen contracts. Download the file template again and use its plan names, which include the plan id.',
        invalid_instrument:
          'Instrument %{instrument} not enabled on plan %{plan} for %{campaign} campaign.',
        amount_column_not_found:
          'Amount column not found in the file, there should be at least one amount.',
        mc_column_not_found:
          'Matching contribution column not found in the file, there should be at least one amount.',
        parsing_file: 'Unknown error during file parsing',
        plan_not_exists: 'Plan %{plan} does not exists.',
        invalid_field: 'Invalid value for field %{field} on line %{line}.',
        invalid_field_no_line: 'Invalid value for field %{field}.',
        negative_amount:
          'Negative amount (%{amount} €) for field %{field} on line %{line}. Allocation amounts must be positive or zero.',
        duplicate_employee:
          'The same affiliate (%{identifier}) appears more than once on lines %{firstLine} and %{line}.',
        missing_day_type: 'The "Type de jour" column is required: no day type on line %{line}.',
        ceiling_reached: 'Ceiling reached',
        missing_consultation_period:
          'A consultation period (start and end date) is required when choices are collected via the app.',
      },
      warnings: {
        name_mismatch:
          'The following names in the file do not match employee records (file → record):',
      },
      consultation_period_description:
        'This is the period in which affiliates can allocated their bonus however they want to. Legally it should last at least 15 days. At the end of the consultation period we will invite to pay the relevant global Operation amount.',
      employee_allocations: 'Employee allocations via file upload',
      employee_allocations_description: {
        part1: 'Your operation creation will only be successful if your ',
        part2bold: "company's affiliates list is correctly completed",
        part3:
          '. If there is a discrepancy between the imported file and the current list of affiliates (additional affiliates), the operation will be ',
        part4bold: 'blocked',
        part5: '. Please check and complete the affiliate list before performing the import.',
      },
      download_sample_empty: 'Download example file here - empty',
      download_sample_current_employees: 'Download example file here - current employees',
      download_sample_all_employees: 'Download example file here - all employees',
      download_sample_all_employees_file: 'Operation All Affiliates Export.xlsm',
      employee_allocations_manual: 'Or manual Employees allocations in next step',
      allocations_removal_warning:
        'Any affiliate missing from a new file is removed from the operation. If they had already made an investment choice, it is cancelled.',
      consultation_period_3_month: 'The consultation period cannot exceed 3 months',
      end_date_after_today: 'Consultation period end date must be at least today',
      late_payment_interests: {
        title: 'Late payment interests',
        subtitle: 'Late payment interests = amount due x TMO x 1.33 x Days of delay/365',
        description:
          'The payment date of your operation is expected to happen %{days} day after the legal deadline. Hence your operation is subjected to late payment interests.',
        formula:
          '{late_payment_interest_amount} € = {amount} x {TMO} x (1+33%) x {Days of delay}/365',
        question: 'Do you want to include these fees into the operation?',
        distribution:
          'If you choose "Yes" the late payment interests will be distributed to affiliates following a pro-rata based on allocation gross amount rule. If you choose "No" you can enter your self-computed amount below or keep it at 0 € to ignore late payment interests.',
        choices: {
          yes: 'Yes',
          no: 'No',
        },
        override_amount: 'Self-computed global amount of late payment interests (optional)',
      },
    },
    vacation_days_allocations: {
      download_title: 'Download allocations made using vacation days',
      download_description:
        'Download the allocations made using the remaining number of vacation days, update them with the corresponding amounts and re-upload the file containing the investment amounts',
      download_link: 'Download allocations',
      upload_title: 'Upload allocations containing the investment amounts',
      upload_description_part1:
        'Add a file containing the list of all recipients with their investments containing all savings products and amounts',
      upload_description_part2:
        "Your operation creation will only be successful if your company's affiliates list is correctly completed. If there is a discrepancy between the imported file and the current list of affiliates (additional affiliates), the operation will be blocked. Please check and complete the affiliate list before performing the import.",
    },
    vacation_days_confirmation: {
      day_type: 'Day type',
      day_types: {
        cet: 'Miscellaneous days',
        rtt: 'RTT',
        overtime: 'Overtime',
        paid_leave: 'Paid leave',
        seniority_leave: 'Seniority leave',
        untaken_rest: 'Untaken rest days',
      },
      invested_amount_in_days: 'Invested days',
      total_net_amount: 'Total net amount',
      net_amount_column: 'Net of CSG/CRDS CET',
    },
    recipients: {
      title: 'Recipients',
      description:
        'Add a file containing the list of all recipients with their total number of days off still remaining',
      affiliates_warning:
        "Your operation creation will only be successful if your company's affiliates list is correctly completed. If there is a discrepancy between the imported file and the current list of employees (additional affiliates), the operation will be blocked. Please check and complete the affiliate list before performing the import.",
      download_sample: 'Download example file here - Employees with remaining vacation days',
      download_sample_file: 'Employees with remaining vacation days.xlsm',
    },
    sfr: {
      title: 'Savings fund request',
    },
    secondary_sfr: {
      title: 'Additional savings fund request',
    },
    payment: {
      title: 'Payment',
      can_not_pay_description:
        'Your company cannot pay for the Operation now. Payment will be available when consultation period is over.',
      payment_method: 'Payment method',
      upload: 'Upload RIB',
      can_pay_description: 'Your company can pay for the operation now.',
      edit_locked: 'The payment method can no longer be changed: %{reason}.',
      cancels_fund_request_warning:
        'Changing the payment method will cancel the savings fund request %{fundRequestId} and issue a new one, with a new "Bordereau de versement".',
    },
  },
  tabs: {
    dashboard: {
      label: 'Dashboard',
    },
    details: {
      label: 'Details',
    },
    documents: {
      label: 'Documents',
      empty: 'This operation has no documents yet',
      fields: {
        affiliates_data: 'Affiliates data',
        allocations: 'Allocations file',
      },
    },
    payouts: {
      label: 'Payouts',
    },
    draft: {
      label: 'Draft',
    },
    in_progress: {
      label: 'In progress',
    },
    history: {
      label: 'History',
    },
    migrated: {
      label: 'Migrated operations',
    },
  },
  migrated: {
    sections: {
      details: 'Details',
    },
    fields: {
      id: 'Operation ID',
      b2b_company_id: 'Company ID',
      company_name: 'Company name',
      status: 'Status',
      type: 'Nature',
      subtype: 'Type',
      created_at: 'Created at',
      fiscal_period_start: 'Fiscal period start date',
      fiscal_period_end: 'Fiscal period end date',
      operation_date: 'Date',
      processing_start_date: 'Consultation start date',
      processing_end_date: 'Consultation end date',
      reference_period: 'Reference period',
      amount_brut: 'Total gross amount to pay',
      amount_net: 'Total net amount to pay',
      source_instrument: 'Source instrument',
      target_instrument: 'Target instrument',
      source_plan_piloted: 'Source plan (piloted)',
      source_plan_libre: 'Source plan (free)',
      target_plan_piloted: 'Target plan (piloted)',
      target_plan_libre: 'Target plan (free)',
      plan_types: 'Plan types',
    },
    choices: {
      status: {
        completed: 'Completed',
      },
      subtype: {
        AUGCAP: 'Capital increase',
        LIBRE: 'Free arbitrage',
        REALLOCATION: 'Reallocation arbitrage',
        ECHLEG: 'Legal deadline',
        CLIENT: 'Withdrawal',
        ECRET: 'Capping',
        ABD: 'Matching contribution',
        INT: 'Incentive bonus',
        PPV: 'Value-sharing bonus',
        RSP: 'Profit-sharing bonus',
        VVENT: "Company's voluntary deposits",
        VVIND: "Employee's voluntary deposit",
        VVP: 'Recurring voluntary deposit',
        GAG_CASH: 'Imported',
        GAG_TITR: 'Imported',
        PER_CASH: 'Outgoing transfer (cash)',
        PER_TITR: 'Outgoing transfer (units)',
        CAPIT: 'Dividend',
        DISTRIB: 'Dividend',
        FUSFONDS: 'Merger',
        INT_CCB: 'Legal deadline',
        IR: 'Late interests',
        NPAI: 'Withdrawal',
        REAJUS: 'Dividend',
        RGL_TITRE: 'Withdrawal',
        SANS_STRESS: 'Switch',
      },
    },
  },
  actions: {
    create: {
      label: 'Create operation',
      success: 'Operation created',
      failure: 'Error creating operation: %{error}',
      csv_field_label: 'Employees allocations CSV file',
    },
    edit: {
      label: 'Edit operation',
      success: 'Operation updated',
      failure: 'Error updating operation: %{error}',
    },
    resume: {
      label: 'Resume',
      dialog_title: 'Resume the operation without the distributor',
    },
    delete: {
      confirm_title: 'Confirm cancellation',
      confirm_content:
        'Are you sure you want to cancel this operation? This action is irreversible.',
      confirm_cancel_ended_campaign:
        'This campaign is ended. Cancelling it will sell back the already-invested positions via correction orders, refunding the original invested amount at the original NAV — any NAV difference is absorbed by AXA. This action is irreversible.',
      label: 'Cancel',
      success: 'Operation marked for cancellation',
      failure: 'Failed to cancel operation: %{error}',
    },
    fetch_mc_remaining_amounts: {
      failure: 'Failed to fetch MC remaining amounts',
    },
  },
  allocations: {
    label: 'Breakdown per eligible employee',
  },
  employees_awaiting_response: {
    label: 'Employees who have not made a choice yet',
  },
  timeline: {
    label: {
      consultation_period: 'Consultation period',
      payment_period: 'Payment period',
      reminder_date: 'Reminder',
    },
  },
};
